/** W82 Design-Web-1 (D-1661): Kopie von `9c93e244:web/src/neu/feder.ts` (Entwurf D-1590), angepasst. Nur die Technik kommt
 *  mit (R-4): Federn aus der geschlossenen Lösung, Wege, Stoß und Puls; keine Gestalt des Entwurfs.
 *  Eine Federphysik für die Bewegungen der Bühne, dieselbe wie die Bühne der Website (buehne.js) und iOS (`BuehneFeder`).
 *  Eine Feder heißt nach Apple mit zwei Werten: Dämpfung ζ (unter 1 schwingt sie über) und Antwort (Periode der
 *  ungedämpften Schwingung in ms, Apples „response“). Aus der geschlossenen Lösung entstehen drei Formen:
 *  - `linear(...)` für CSS-Übergänge (je Bild abgetastet, dazwischen linear), als Variablen `--feder-<name>` an :root;
 *  - Motion-Übergänge `{ type: "spring", stiffness, damping }` für Blätter, Knöpfe und Gesten (tragen Geschwindigkeit);
 *  - abgetastete Schlüsselbilder für die Web Animations API (Flüge, Wege, Ablegen), die im Compositor laufen.
 *  Ruhig (Fassung) und reduzierte Bewegung nehmen kritisch gedämpfte Fassungen bzw. nur Blenden. */
import type { Transition } from "motion/react";

export type Feder = ((t: number) => number) & { dauer: number; erreicht: number; zeta: number; antwort: number; v0: number };

export const klemme = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);
export const mix = (a: number, b: number, t: number) => a + (b - a) * t;
export const glatt = (t: number) => {
  const x = klemme(t, 0, 1);
  return x * x * (3 - 2 * x);
};

/** Gedämpfte Feder von 0 nach 1 (geschlossene Lösung wie buehne.js). `v0` Anfangsgeschwindigkeit in 1/ms. Liefert f(t) mit
 *  `dauer` (bis zur Ruhe, Rest unter 0,2 Prozent, die letzten 20 Prozent weich auf 1 gezogen) und `erreicht` (erstes Mal am
 *  Ziel, bei ζ ≥ 1 bei 98,5 Prozent). */
export function feder(zeta: number, antwort: number, v0 = 0): Feder {
  const w = (2 * Math.PI) / antwort;
  let roh: (t: number) => number;
  let dauer: number;
  if (zeta < 1) {
    const wd = w * Math.sqrt(1 - zeta * zeta);
    const b = (v0 - zeta * w) / wd;
    roh = (t) => 1 + Math.exp(-zeta * w * t) * (-Math.cos(wd * t) + b * Math.sin(wd * t));
    dauer = 6.2 / (zeta * w);
  } else {
    roh = (t) => 1 + (-1 + (v0 - w) * t) * Math.exp(-w * t);
    dauer = 8.6 / w;
  }
  const f = ((t: number) => {
    if (t <= 0) return 0;
    if (t >= dauer) return 1;
    const x = roh(t);
    const r = (t - dauer * 0.8) / (dauer * 0.2);
    return r > 0 ? mix(x, 1, glatt(r)) : x;
  }) as Feder;
  f.dauer = dauer;
  f.erreicht = dauer;
  f.zeta = zeta;
  f.antwort = antwort;
  f.v0 = v0;
  const schwelle = zeta < 1 ? 1 : 0.985;
  for (let t = 0; t < dauer; t += 2) {
    if (roh(t) >= schwelle) {
      f.erreicht = t;
      break;
    }
  }
  return f;
}

/** Benannte Federn der App (DESIGN-NEU-WEB.md „Bewegungsregeln“). ζ und Antwort in ms; `ruhig` ist die Fassung der ruhigen
 *  Welt (kritisch gedämpft, etwas langsamer), damit keine Bewegung dort überschwingt. */
export const FEDERN = {
  /** Lage und Größe ändern sich (Layout, Umsetzen): Apple „move“ 1,0 / 0,4 s */
  ruhe: { zeta: 1, antwort: 420 },
  /** Blatt mobil von unten (Apple „drawer“ 0,8 / 0,3 s, auf Papier etwas schwerer) */
  blatt: { zeta: 0.84, antwort: 440 },
  /** Dialog am Desktop: hebt sich vom Tisch */
  dialog: { zeta: 0.8, antwort: 500 },
  /** Knopf loslassen, kleines Aufploppen */
  knopf: { zeta: 0.58, antwort: 360 },
  /** Licht unter dem aktiven Reiter, Segment, Schalter */
  pille: { zeta: 0.76, antwort: 460 },
  /** Weg eines fliegenden Briefs (Bühne: feder(0,72; 1,05 bis 1,21 s)) */
  papier: { zeta: 0.72, antwort: 1080 },
  /** Ein Brief legt sich flach (Bühne: feder(0,46; 500 ms)) */
  flach: { zeta: 0.46, antwort: 500 },
  /** Fang und Anstoßen (Bühne: Brief feder(0,55; 1 560 ms) am Ziel nach 640 ms; Rückfedern feder(0,5; 640 ms)) */
  fang: { zeta: 0.5, antwort: 640 },
  /** Landung des Gaists (Bühne: feder(0,5; 420 ms)) */
  landen: { zeta: 0.5, antwort: 420 },
  /** Sortierflug in Kachel oder Akte (Bühne: feder(0,85; 660 ms)) */
  ordnen: { zeta: 0.85, antwort: 660 },
  /** Übergabe, Seitenwechsel mit gemeinsamem Element (Bühne: feder(0,8; 760 ms)) */
  uebergabe: { zeta: 0.8, antwort: 760 },
  /** Zählwerk: kritisch gedämpft, immer vorwärts (Bühne: Antwort 460 ms) */
  zaehlen: { zeta: 1, antwort: 460 },
  /** Aufsteigen des Gaists (Bühne: feder(0,62; 900 ms)) */
  steigen: { zeta: 0.62, antwort: 900 },
} as const;
export type FederName = keyof typeof FEDERN;

/** Die Feder eines Namens, in der ruhigen Fassung kritisch gedämpft und 15 Prozent langsamer. */
export function federVon(name: FederName, ruhig = false, v0 = 0): Feder {
  const { zeta, antwort } = FEDERN[name];
  return ruhig ? feder(Math.max(1, zeta), antwort * 1.15, v0) : feder(zeta, antwort, v0);
}

/** Motion-Übergang aus ζ und Antwort (Masse 1): Steifigkeit (2π/T)², Dämpfung 2ζ(2π/T). */
export function federMotion(name: FederName, ruhig = false, velocity?: number): Transition {
  const { zeta, antwort } = FEDERN[name];
  const z = ruhig ? Math.max(1, zeta) : zeta;
  const w = (2 * Math.PI) / ((ruhig ? antwort * 1.15 : antwort) / 1000);
  return { type: "spring", stiffness: w * w, damping: 2 * z * w, mass: 1, ...(velocity !== undefined ? { velocity } : {}), restDelta: 0.2, restSpeed: 2 };
}

/** CSS `linear()` einer Feder: Stützpunkte, wo die Kurve sich biegt, höchstens `punkte` viele (Chrome und Safari können
 *  linear() seit 2023). Rückgabe der Wert und die Dauer in ms. */
export function federLinear(f: Feder, punkte = 44): { kurve: string; dauer: number } {
  const n = Math.max(8, punkte);
  const werte: string[] = [];
  for (let i = 0; i <= n; i++) {
    // dichter am Anfang, wo sich die Feder am stärksten biegt
    const p = Math.pow(i / n, 1.35);
    const y = f(p * f.dauer);
    werte.push(i === 0 ? "0" : i === n ? "1" : `${y.toFixed(4)} ${(p * 100).toFixed(2)}%`);
  }
  return { kurve: `linear(${werte.join(", ")})`, dauer: Math.round(f.dauer) };
}

/** Schreibt je Feder `--feder-<name>` (Kurve) und `--feder-<name>-dauer` (ms) an :root, lebhaft und ruhig
 *  (`--feder-<name>-ruhig`). Einmal beim Start (main.tsx); ohne linear() bleiben die Rückfallwerte aus neu.css. */
export function federnAnwenden(): void {
  if (typeof document === "undefined") return;
  if (!(typeof CSS !== "undefined" && CSS.supports?.("transition-timing-function", "linear(0, 1)"))) return;
  const s = document.documentElement.style;
  // die ruhige Fassung schwingt nie über: dann tragen die Hauptnamen die kritisch gedämpften Federn
  const ruhigeWelt = ruhigeFassung();
  for (const name of Object.keys(FEDERN) as FederName[]) {
    const lebhaft = federLinear(federVon(name));
    const ruhig = federLinear(federVon(name, true));
    const haupt = ruhigeWelt ? ruhig : lebhaft;
    s.setProperty(`--feder-${name}`, haupt.kurve);
    s.setProperty(`--feder-${name}-dauer`, `${haupt.dauer}ms`);
    s.setProperty(`--feder-${name}-ruhig`, ruhig.kurve);
    s.setProperty(`--feder-${name}-ruhig-dauer`, `${ruhig.dauer}ms`);
  }
}

// ---------------------------------------------------------------- Wege, Stoß, Puls (wie buehne.js)

export type P = [number, number];

/** Quadratische Bézierkurve; über das Ende hinaus (Überschwingen der Feder) entlang der Tangente, gedämpft mit k. */
export function bogen2(a: P, c: P, b: P, k = 0.5): (p: number) => P {
  return (p) => {
    if (p <= 1) {
      const q = 1 - p;
      return [q * q * a[0] + 2 * q * p * c[0] + p * p * b[0], q * q * a[1] + 2 * q * p * c[1] + p * p * b[1]];
    }
    const d = (p - 1) * 2 * k;
    return [b[0] + (b[0] - c[0]) * d, b[1] + (b[1] - c[1]) * d];
  };
}

/** Kubische Bézierkurve, über das Ende hinaus wie `bogen2`. */
export function bogen3(a: P, c1: P, c2: P, b: P, k = 0.5): (p: number) => P {
  return (p) => {
    if (p <= 1) {
      const q = 1 - p;
      const w0 = q * q * q, w1 = 3 * q * q * p, w2 = 3 * q * p * p, w3 = p * p * p;
      return [w0 * a[0] + w1 * c1[0] + w2 * c2[0] + w3 * b[0], w0 * a[1] + w1 * c1[1] + w2 * c2[1] + w3 * b[1]];
    }
    const d = (p - 1) * 3 * k;
    return [b[0] + (b[0] - c2[0]) * d, b[1] + (b[1] - c2[1]) * d];
  };
}

/** Weicher Stoß: steigt in tau auf 1 und klingt aus. */
export const stoss = (t: number, tau: number) => (t <= 0 ? 0 : (t / tau) * Math.exp(1 - t / tau));

/** cubic-bezier wie in CSS als Funktion (Newton, sonst Halbierung). */
export function kubisch(x1: number, y1: number, x2: number, y2: number): (x: number) => number {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const fx = (t: number) => ((ax * t + bx) * t + cx) * t;
  const fy = (t: number) => ((ay * t + by) * t + cy) * t;
  const dfx = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const e = fx(t) - x;
      const d = dfx(t);
      if (Math.abs(e) < 1e-6 || Math.abs(d) < 1e-6) break;
      t -= e / d;
    }
    if (t < 0 || t > 1 || Math.abs(fx(t) - x) > 1e-4) {
      let a = 0, b = 1;
      for (let i = 0; i < 32; i++) {
        t = (a + b) / 2;
        if (fx(t) < x) a = t;
        else b = t;
      }
    }
    return fy(t);
  };
}
export const EXPO = kubisch(0.16, 1, 0.3, 1);
export const SANFT = kubisch(0.45, 0, 0.55, 1);
/** Puls: in `an` ms hinauf (expo), in `ab` ms hinunter (sanft). */
export const puls = (t: number, an: number, ab: number) => (t <= 0 || t >= an + ab ? 0 : t < an ? EXPO(t / an) : 1 - SANFT((t - an) / ab));

// ---------------------------------------------------------------- Abspielen (Web Animations API)

export function bewegungReduziert(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Prüfschalter `?still`: alles sofort im Endzustand (Bilder, Tests). */
export function stillGeschaltet(): boolean {
  return typeof location !== "undefined" && new URLSearchParams(location.search).has("still");
}

export function ruhigeFassung(): boolean {
  return typeof document !== "undefined" && document.documentElement.dataset.fassung === "ruhig";
}

/** Tastet eine Zustandsfunktion ab und spielt sie als eine Animation je Ziel. `zeit` [t0, t1] in ms, `hz` Bilder je
 *  Sekunde (40 bis 60), `zustand(t)` liefert je Schlüssel ein Schlüsselbild; `ziele` ordnet den Schlüsseln Elemente zu.
 *  `fill: both` hält Anfang und Ende; die Rückgabe sind die Animationen (zum Abbrechen). */
export function spur(
  zeit: [number, number],
  hz: number,
  zustand: (t: number) => Record<string, Keyframe>,
  ziele: Record<string, Element | null | undefined>,
  opt: { verzug?: number } = {},
): Animation[] {
  const [t0, t1] = zeit;
  const n = Math.max(2, Math.ceil(((t1 - t0) * hz) / 1000));
  const bilder: Record<string, Keyframe[]> = {};
  for (const k of Object.keys(ziele)) bilder[k] = [];
  for (let i = 0; i <= n; i++) {
    const t = t0 + ((t1 - t0) * i) / n;
    const w = zustand(t);
    for (const k of Object.keys(ziele)) {
      const b = w[k];
      if (b) bilder[k]?.push({ ...b, offset: i / n });
    }
  }
  const laufend: Animation[] = [];
  for (const [k, el] of Object.entries(ziele)) {
    const b = bilder[k];
    if (!el || !b?.length || typeof (el as HTMLElement).animate !== "function") continue;
    laufend.push((el as HTMLElement).animate(b, { delay: (opt.verzug ?? 0) + t0, duration: Math.max(1, t1 - t0), fill: "both", easing: "linear" }));
  }
  return laufend;
}

/** Eine Feder von `von` nach `nach` für eine Größe; `bild(wert)` baut daraus ein Schlüsselbild. Für einfache Wege. */
export function federBilder(f: Feder, bild: (wert: number) => Keyframe, hz = 60): Keyframe[] {
  const n = Math.max(2, Math.ceil((f.dauer * hz) / 1000));
  const out: Keyframe[] = [];
  for (let i = 0; i <= n; i++) out.push({ ...bild(f((f.dauer * i) / n)), offset: i / n });
  return out;
}

/** Spielt eine Feder auf einem Element (transform, opacity) und lässt es im Endzustand; reduziert nur eine Blende von
 *  höchstens 150 ms, `?still` sofort. */
export function federSpiel(
  el: HTMLElement | SVGElement | null | undefined,
  name: FederName,
  bild: (wert: number) => Keyframe,
  opt: { verzug?: number; v0?: number; ende?: Keyframe } = {},
): Animation | null {
  if (!el || typeof el.animate !== "function") return null;
  if (stillGeschaltet()) return null;
  if (bewegungReduziert()) return el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: "linear", delay: 0, fill: "backwards" });
  const f = federVon(name, ruhigeFassung(), opt.v0 ?? 0);
  return el.animate(federBilder(f, bild), { duration: f.dauer, delay: opt.verzug ?? 0, easing: "linear", fill: "backwards" });
}

// ---------------------------------------------------------------- Sekunden-Federn der Eröffnung (iOS `BuehneFeder`, d07b42f7)

/** Dieselbe Feder mit Zeit in Sekunden (Antwort in s, v0 in 1/s), wie `BuehneFeder` in BuehneBewegung.swift. */
export type FederS = ((t: number) => number) & { dauer: number; erreicht: number };
export function federS(zeta: number, antwort: number, v0 = 0): FederS {
  const f = feder(zeta, antwort * 1000, v0 / 1000);
  const g = ((t: number) => f(t * 1000)) as FederS;
  g.dauer = f.dauer / 1000;
  g.erreicht = f.erreicht / 1000;
  return g;
}

/** Federn der Eröffnung (design-neu-ios.md „Bewegungsregeln“, BuehneBewegung.swift:179-203): Dämpfung und Antwort in s. */
export const BUEHNE = {
  flachlegen: federS(0.46, 0.5),
  steigen: federS(0.62, 0.9),
  fang: federS(0.55, 1.56),
  rueckfedern: federS(0.5, 0.64, 4),
  aufprall: federS(0.38, 0.52),
  uebergabe: federS(0.8, 0.76),
  umdrehen: federS(0.78, 0.56),
  lasche: federS(0.6, 0.62),
  blitz: federS(0.42, 0.64),
  einordnen: federS(0.85, 0.66),
  landen: federS(0.5, 0.42),
  zaehlwerk: federS(1, 0.46),
} as const;

/** Deterministischer Zufall (mulberry32 wie Website und iOS `Buehne.Zufall`): der Haufen fällt bei jedem Aufruf gleich. */
export function zufall(saat: number): (() => number) & { gauss: () => number } {
  let s = saat >>> 0;
  const r = (() => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), 1 | t);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }) as (() => number) & { gauss: () => number };
  r.gauss = () => (r() + r() + r() - 1.5) / 1.5;
  return r;
}

